import React, { type ChangeEvent, useCallback } from 'react';
import { TextField } from '@mui/material';
import { type HTMLInputElementExtended } from '@innovyze/stylovyze';

const parseYearValue = (value: any): string => {
	if (value === null || value === undefined) {
		return '';
	}

	// Handle string values
	if (typeof value === 'string') {
		// If it's an empty string, return it as-is (don't fall back)
		if (value === '') {
			return '';
		}
		// Check if it's a datetime string
		if (value.includes('T') && value.includes('Z')) {
			const year = new Date(value).getFullYear();
			if (!isNaN(year)) return year.toString();
		}
		// If it's a raw number string, return it as-is
		return value;
	}

	// Handle array values (legacy support)
	if (Array.isArray(value) && value.length) {
		const year = new Date(value[0]).getFullYear();
		if (!isNaN(year)) return year.toString();
	}

	return '';
};

interface YearFieldProps {
	fieldName: string;
	label: string;
	formValue: any;
	initialValue: any;
	formRef: React.RefObject<any>;
	disabled?: boolean;
	error?: boolean;
	helperText?: string | null;
}

export const YearField: React.FC<YearFieldProps> = ({
	fieldName,
	label,
	formValue,
	initialValue,
	formRef,
	disabled = false,
	error = false,
	helperText = null,
}) => {
	const getYearValue = useCallback(
		(formValue: any, initialValue: any): string => {
			// If formValue is explicitly set (even if empty string), use it
			if (formValue !== null && formValue !== undefined) {
				return parseYearValue(formValue);
			}

			// Only fall back to initial value if formValue was never set
			return parseYearValue(initialValue);
		},
		[],
	);

	const setYearFieldValue = useCallback(
		(
			field: string,
			event: ChangeEvent<HTMLInputElementExtended>,
			formRef: React.RefObject<any>,
		) => {
			// Store the raw input value without validation
			formRef?.current?.setFieldValue(field, event.target.value);
		},
		[],
	);

	const validateAndSetYearFieldValue = useCallback(
		(
			field: string,
			event: ChangeEvent<HTMLInputElementExtended>,
			formRef: React.RefObject<any>,
		) => {
			let value = parseFloat(event.target.value);
			// Do this because smaller values i.e. 18 just get made into 1918
			if (isNaN(value) || value < 1900 || value >= 2100)
				value = value >= 2100 ? 2099 : 1900;
			const date = new Date(value, 0, 1, 0, 0);
			const dateString = date.toISOString();

			formRef?.current?.setFieldValue(field, dateString);
		},
		[],
	);

	const displayValue = getYearValue(formValue, initialValue);

	return (
		<TextField
			fullWidth
			disabled={disabled}
			name={fieldName}
			value={displayValue}
			onChange={event => {
				setYearFieldValue(
					fieldName,
					event as ChangeEvent<HTMLInputElementExtended>,
					formRef,
				);
			}}
			onBlur={event => {
				validateAndSetYearFieldValue(
					fieldName,
					event as ChangeEvent<HTMLInputElementExtended>,
					formRef,
				);
			}}
			label={label}
			variant="outlined"
			type="number"
			inputProps={{
				min: 1900,
				max: 2200,
				step: 1,
			}}
			error={error}
			helperText={helperText}
		/>
	);
};
